import SupportingDocumentItem, {
  SupportingDocument,
} from '@/features/prescriptions/SupportingDocumentItem'
import { useStore } from '@/lib/store'
import React, { ChangeEvent, useState } from 'react'
import {
  NotificationItem,
  NotificationItemVariant,
} from '@/lib/models/NotificationItem'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilesMedical } from '@fortawesome/pro-duotone-svg-icons'
import FileInputButton from '@/shared/components/buttons/FileInputButton'
import {
  faPlus,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons'
import { Dialog } from '@headlessui/react'
import TextButton from '@/shared/components/buttons/TextButton'
import { DialogModal } from '@/shared/components/DialogModal'

export type UploadDocumentContainersProps = {
  documents: Array<SupportingDocument>
  onAddDocument: (document: SupportingDocument) => void
  onDeleteDocument: (name: string) => void
  onUpdateDescription: (name: string, description: string) => void
}

export default function UploadDocumentsContainer({
  documents,
  onAddDocument,
  onDeleteDocument,
  onUpdateDescription,
}: UploadDocumentContainersProps) {
  const showNotification = useStore((state) => state.showNotification)
  const [documentToDelete, setDocumentToDelete] = useState('')

  function onUploadDocument(ev: ChangeEvent<HTMLInputElement>) {
    if (!ev.target.files) return

    const file = ev.target.files[0]

    if (file.size > 5242880) {
      showNotification(
        new NotificationItem(
          'File size must be less than 5MB',
          NotificationItemVariant.Error,
        ),
      )
      return
    }

    if (documents.findIndex((document) => document.name === file.name) !== -1) {
      showNotification(
        new NotificationItem(
          `There's already a document with name ${file.name}`,
          NotificationItemVariant.Error,
        ),
      )
      return
    }

    const url = URL.createObjectURL(file)

    onAddDocument({
      url: url,
      name: file.name,
      type: file.type,
      description: '',
      uploadState: {
        uploading: true,
        progress: 0,
      },
    })
  }

  return (
    <div className="flex w-full flex-col gap-y-3">
      <p className="leading-6 text-gray-800">
        You may optionally add any supporting photos or documents for the
        doctors to review. (5MB max)
      </p>
      {documents.length === 0 ? (
        <div className="flex flex-col gap-y-3">
          <div className="flex flex-col items-center justify-center gap-y-3 border border-dashed border-gray-300 p-14">
            <div className="text-slate-500">
              <FontAwesomeIcon icon={faFilesMedical} size="2xl" />
            </div>
            <h3 className="font-medium leading-7 text-gray-900">
              No supporting document
            </h3>
            <FileInputButton
              id="file-upload"
              accept="image/png,image/jpeg,.pdf"
              onChange={onUploadDocument}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add document
            </FileInputButton>
          </div>
          <p className="text-center text-sm font-medium text-gray-500">
            Supported formats: PDF, JPEG, PNG
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-y-4">
          <ul className="flex w-full grow flex-col gap-y-3">
            {documents.map((document) => (
              <SupportingDocumentItem
                key={document.name}
                document={document}
                editable={true}
                onChangeDescription={(description) =>
                  onUpdateDescription(document.name, description)
                }
                onRemoveDocument={() => setDocumentToDelete(document.name)}
                enableDownload={false}
              />
            ))}
          </ul>
          <FileInputButton
            id="file-upload"
            accept="image/png,image/jpeg,.pdf"
            onChange={onUploadDocument}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add another
          </FileInputButton>
          <p className="text-center text-sm font-medium text-gray-500">
            Supported formats: PDF, JPEG, PNG
          </p>
        </div>
      )}
      <DialogModal
        show={documentToDelete.length > 0}
        onClose={() => setDocumentToDelete('')}
      >
        <div className="flex flex-col justify-center gap-y-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <FontAwesomeIcon
              className="h-6 w-6 text-red-600"
              aria-hidden="true"
              icon={faTriangleExclamation}
            />
          </div>
          <div className="flex flex-col gap-y-2.5">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              Delete {documentToDelete}?
            </Dialog.Title>
            <p className="text-sm text-gray-500">
              Are you sure you want to delete the document{' '}
              <span className="font-bold">{documentToDelete}</span>? This will
              also remove the description for this document.
            </p>
          </div>
          <div className="flex w-full flex-1 items-center gap-x-3">
            <TextButton
              className="grow"
              title="Confirm"
              color="red"
              onClick={() => {
                onDeleteDocument(documentToDelete)
                setDocumentToDelete('')
              }}
            />
            <TextButton
              className="grow"
              title="Cancel"
              color="gray"
              appearance="ring"
              onClick={() => setDocumentToDelete('')}
            />
          </div>
        </div>
      </DialogModal>
    </div>
  )
}
